import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useSmsCtaHref } from "@/components/TextUs";


const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Rentals", href: "/rentals" },
  { label: "Private Jet", href: "/private-jet-car-service-atlanta" },
  { label: "Fleet", href: "#fleet" },
  { label: "Airport", href: "#airport" },
  { label: "Text Maria", href: "#booking" },
];

function useHashHref() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (href: string) => (pathname === "/" ? href : `/${href}`);
}


export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const hashHref = useHashHref();
  const smsCta = useSmsCtaHref();
  const navHref = (link: { label: string; href: string }) => (smsCta && link.label === "Text Maria" ? smsCta : hashHref(link.href));
  const ctaAttr = (link: { label: string }) => (smsCta && link.label === "Text Maria" ? { "data-sms-cta": "" } : {});

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3">
            <span className="font-heading text-xl font-semibold tracking-tight text-foreground">
              Atlanta Luxury Sprinters
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) =>
              link.href.startsWith("/") ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium tracking-widest uppercase"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={navHref(link)}
                  {...ctaAttr(link)}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium tracking-widest uppercase"
                >
                  {link.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+16784317222"
              className="flex items-center gap-2 text-primary text-sm font-medium"
            >
              <Phone className="w-4 h-4" />
              (678) 431-7222
            </a>
            <a href={smsCta ?? hashHref("#booking")} className="btn-primary rounded-sm">
              Text Maria
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-foreground"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-t border-border"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) =>
                link.href.startsWith("/") ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="block text-foreground text-sm font-medium tracking-widest uppercase py-2"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={navHref(link)}
                  {...ctaAttr(link)}
                    onClick={() => setMobileOpen(false)}
                    className="block text-foreground text-sm font-medium tracking-widest uppercase py-2"
                  >
                    {link.label}
                  </a>
                )
              )}
              <a
                href="tel:+16784317222"
                className="flex items-center gap-2 text-primary text-sm font-medium pt-4 border-t border-border"
              >
                <Phone className="w-4 h-4" />
                (678) 431-7222
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
